import React, { useEffect, useState, useRef } from 'react';
import { TerminalIcon } from 'lucide-react';
interface TerminalLine {
  text: string;
  type: 'command' | 'success' | 'info' | 'warning' | 'error' | 'highlight' | 'progress' | 'ascii';
  delay: number;
}
const initialLines: TerminalLine[] = [{
  text: '$ initializing gemini_core_v2.4.1...',
  type: 'command',
  delay: 0
}, {
  text: '[OK] Vector database connected (128-dim)',
  type: 'success',
  delay: 800
}, {
  text: '[OK] Multimodal embeddings synced',
  type: 'success',
  delay: 1400
}, {
  text: '[OK] Semantic routing active',
  type: 'success',
  delay: 2000
}, {
  text: 'Waiting for diagnostic trigger...',
  type: 'highlight',
  delay: 2600
}];
const diagnosticLines: TerminalLine[] = [{
  text: '',
  type: 'info',
  delay: 0
}, {
  text: '$ sudo gemini-diag --full --verbose',
  type: 'command',
  delay: 200
}, {
  text: '',
  type: 'info',
  delay: 600
}, {
  text: '╔══════════════════════════════════════════╗',
  type: 'ascii',
  delay: 800
}, {
  text: '║   GEMINI MASTER DIAGNOSTICS v2.4.1      ║',
  type: 'ascii',
  delay: 900
}, {
  text: '╚══════════════════════════════════════════╝',
  type: 'ascii',
  delay: 1000
}, {
  text: '',
  type: 'info',
  delay: 1200
}, {
  text: '[1/8] Scanning neural pathways...',
  type: 'info',
  delay: 1400
}, {
  text: '  ████████████████████████████████ 100%',
  type: 'progress',
  delay: 2200
}, {
  text: '  → 2,847 pathways active, 0 degraded',
  type: 'success',
  delay: 2600
}, {
  text: '',
  type: 'info',
  delay: 2800
}, {
  text: '[2/8] Testing multimodal input channels...',
  type: 'info',
  delay: 3000
}, {
  text: '  TEXT    ✓  latency: 12ms   throughput: 45k req/s',
  type: 'success',
  delay: 3600
}, {
  text: '  IMAGE   ✓  latency: 28ms   throughput: 12k req/s',
  type: 'success',
  delay: 4000
}, {
  text: '  VIDEO   ✓  latency: 45ms   throughput: 3.2k req/s',
  type: 'success',
  delay: 4400
}, {
  text: '  AUDIO   ✓  latency: 18ms   throughput: 22k req/s',
  type: 'success',
  delay: 4800
}, {
  text: '',
  type: 'info',
  delay: 5000
}, {
  text: '[3/8] Validating embedding space...',
  type: 'info',
  delay: 5200
}, {
  text: '  ████████████████████████████████ 100%',
  type: 'progress',
  delay: 6000
}, {
  text: '  → Cosine similarity: 0.9847 (excellent)',
  type: 'success',
  delay: 6400
}, {
  text: '',
  type: 'info',
  delay: 6600
}, {
  text: '[4/8] Cross-modal retrieval benchmark...',
  type: 'info',
  delay: 6800
}, {
  text: '  Text→Image: 96.2% accuracy',
  type: 'success',
  delay: 7400
}, {
  text: '  Image→Text: 94.8% accuracy',
  type: 'success',
  delay: 7800
}, {
  text: '  Audio→Text: 92.1% accuracy',
  type: 'success',
  delay: 8200
}, {
  text: '  Video→All:  91.7% accuracy',
  type: 'success',
  delay: 8600
}, {
  text: '',
  type: 'info',
  delay: 8800
}, {
  text: '[5/8] Memory allocation check...',
  type: 'info',
  delay: 9000
}, {
  text: '  GPU VRAM: 78.4 / 80 GB allocated',
  type: 'warning',
  delay: 9600
}, {
  text: '  System RAM: 124 / 256 GB used',
  type: 'success',
  delay: 10000
}, {
  text: '',
  type: 'info',
  delay: 10200
}, {
  text: '[6/8] Security & encryption audit...',
  type: 'info',
  delay: 10400
}, {
  text: '  ████████████████████████████████ 100%',
  type: 'progress',
  delay: 11200
}, {
  text: '  → AES-256 active, TLS 1.3, zero vulnerabilities',
  type: 'success',
  delay: 11600
}, {
  text: '',
  type: 'info',
  delay: 11800
}, {
  text: '[7/8] PWA service worker integrity...',
  type: 'info',
  delay: 12000
}, {
  text: '  Cache: 847 assets precached',
  type: 'success',
  delay: 12600
}, {
  text: '  Offline: fully operational',
  type: 'success',
  delay: 13000
}, {
  text: '  Push: 99.97% delivery rate',
  type: 'success',
  delay: 13400
}, {
  text: '',
  type: 'info',
  delay: 13600
}, {
  text: '[8/8] Final system health score...',
  type: 'info',
  delay: 13800
}, {
  text: '  ████████████████████████████████ 100%',
  type: 'progress',
  delay: 14600
}, {
  text: '',
  type: 'info',
  delay: 15000
}, {
  text: '╔══════════════════════════════════════════╗',
  type: 'ascii',
  delay: 15200
}, {
  text: '║  ✅ ALL SYSTEMS OPERATIONAL              ║',
  type: 'ascii',
  delay: 15400
}, {
  text: '║  Health Score: 98.7 / 100               ║',
  type: 'ascii',
  delay: 15600
}, {
  text: '║  Status: PRODUCTION READY                ║',
  type: 'ascii',
  delay: 15800
}, {
  text: '╚══════════════════════════════════════════╝',
  type: 'ascii',
  delay: 16000
}, {
  text: '',
  type: 'info',
  delay: 16200
}, {
  text: '$ gemini-core ready. Awaiting commands...',
  type: 'highlight',
  delay: 16400
}];
export function InteractiveTerminal() {
  const [visibleLines, setVisibleLines] = useState<TerminalLine[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [isDiagComplete, setIsDiagComplete] = useState(false);
  const [typingIndex, setTypingIndex] = useState(0);
  const [currentTyping, setCurrentTyping] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  // Initial boot sequence with typing effect
  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    initialLines.forEach((line, i) => {
      const t = setTimeout(() => {
        setVisibleLines(prev => [...prev, line]);
      }, line.delay);
      timers.push(t);
    });
    timersRef.current = timers;
    return () => timers.forEach(clearTimeout);
  }, []);
  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [visibleLines]);
  const runDiagnostics = () => {
    if (isRunning || isDiagComplete) return;
    setIsRunning(true);
    const timers: ReturnType<typeof setTimeout>[] = [];
    diagnosticLines.forEach((line, i) => {
      const t = setTimeout(() => {
        setVisibleLines(prev => [...prev, line]);
        if (i === diagnosticLines.length - 1) {
          setIsRunning(false);
          setIsDiagComplete(true);
        }
      }, line.delay);
      timers.push(t);
    });
    timersRef.current = [...timersRef.current, ...timers];
  };
  const resetTerminal = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
    setVisibleLines([]);
    setIsRunning(false);
    setIsDiagComplete(false);
    const timers: ReturnType<typeof setTimeout>[] = [];
    initialLines.forEach(line => {
      const t = setTimeout(() => {
        setVisibleLines(prev => [...prev, line]);
      }, line.delay);
      timers.push(t);
    });
    timersRef.current = timers;
  };
  const getLineColor = (type: TerminalLine['type']) => {
    switch (type) {
      case 'command':
        return 'text-gray-400';
      case 'success':
        return 'text-emerald-400';
      case 'info':
        return 'text-gray-500';
      case 'warning':
        return 'text-amber-400';
      case 'error':
        return 'text-red-400';
      case 'highlight':
        return 'text-cyan-400';
      case 'progress':
        return 'text-apple-blue';
      case 'ascii':
        return 'text-emerald-300';
      default:
        return 'text-gray-400';
    }
  };
  return <div className="max-w-4xl mx-auto mb-36" data-id="element-168" data-mp="2mmwxvvxn1kp">
      <div className="bg-[#0d1117] rounded-2xl overflow-hidden shadow-2xl border border-gray-800/50 relative" data-id="element-169" data-mp="2lv4uv1jnlz6">
        {/* Scanline overlay */}
        <div className="absolute inset-0 pointer-events-none z-10 opacity-[0.03]" style={{
        background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.1) 2px, rgba(255,255,255,0.1) 4px)'
      }} data-id="element-170" data-mp="2w58qpxaf3h80" />

        {/* Title bar */}
        <div className="flex items-center px-5 py-3 border-b border-gray-800 bg-[#161b22] relative z-20" data-id="element-171" data-mp="313tsi15bi8yk">
          <div className="flex gap-2" data-id="element-172" data-mp="3f3bn4x5aogfu">
            <div className="w-3 h-3 rounded-full bg-red-500/80 hover:bg-red-500 transition-colors cursor-pointer" data-id="element-173" data-mp="3f29z7s36jzgp" />
            <div className="w-3 h-3 rounded-full bg-yellow-500/80 hover:bg-yellow-500 transition-colors cursor-pointer" data-id="element-174" data-mp="3oufylmvrhj5x" />
            <div className="w-3 h-3 rounded-full bg-green-500/80 hover:bg-green-500 transition-colors cursor-pointer" data-id="element-175" data-mp="3jv336dqtr3rq" />
          </div>
          <div className="mx-auto flex items-center gap-2 text-gray-500 text-xs font-mono" data-id="element-176" data-mp="1q9je3aykaqo">
            <TerminalIcon className="w-3.5 h-3.5" data-id="element-177" data-mp="3tv3r6czn2829" />
            system_diagnostics.sh
          </div>
          <div className="flex items-center gap-2" data-id="element-178" data-mp="gcykuaorsxzp">
            {isRunning && <span className="text-xs font-mono text-emerald-400 animate-pulse" data-id="element-179" data-mp="lagvq6mclnac">
                ● RUNNING
              </span>}
            {isDiagComplete && <span className="text-xs font-mono text-emerald-400" data-id="element-180" data-mp="cabxon31o18u">
                ● COMPLETE
              </span>}
          </div>
        </div>

        {/* Terminal body */}
        <div ref={scrollRef} className="p-6 font-mono text-sm leading-relaxed overflow-y-auto relative z-20" style={{
        maxHeight: '480px',
        minHeight: '280px'
      }} data-id="element-181" data-mp="2ce1ndey105o">
          {visibleLines.map((line, i) => <div key={i} className={`${getLineColor(line.type)} transition-opacity duration-300`} style={{
          animation: 'fadeInTerminal 0.3s ease-out'
        }} data-id="element-182" data-mp="3opvzwr81p2nk" data-mpi={i}>
              {line.text === '' ? <br data-id="element-183" data-mp="3f891irl338vu" /> : line.text}
            </div>)}

          {/* Blinking cursor */}
          <div className="inline-flex items-center mt-1" data-id="element-184" data-mp="3jbf1jhhls3tv">
            <span className="text-gray-500" data-id="element-185" data-mp="35b4buhs14vaa">$</span>
            <span className="ml-2 w-2.5 h-5 bg-emerald-400 inline-block" style={{
            animation: 'blink 1s step-end infinite'
          }} data-id="element-186" data-mp="35cfh6m14ib3i" />
          </div>
        </div>

        {/* Action bar */}
        <div className="px-6 py-5 border-t border-gray-800 bg-[#161b22] flex items-center justify-between relative z-20" data-id="element-187" data-mp="6tqmg5wq6t2q">
          <div className="flex items-center gap-3" data-id="element-188" data-mp="3o8p3fwm6i7yl">
            <div className={`w-2 h-2 rounded-full ${isDiagComplete ? 'bg-emerald-400' : isRunning ? 'bg-amber-400 animate-pulse' : 'bg-gray-600'}`} data-id="element-189" data-mp="3t8tirjbcown8" />
            <span className="text-xs font-mono text-gray-500" data-id="element-190" data-mp="krovqww6vzbd">
              {isDiagComplete ? 'Health: 98.7/100 — Production Ready' : isRunning ? 'Running diagnostics...' : 'System idle'}
            </span>
          </div>
          <div className="flex gap-3" data-id="element-191" data-mp="25asff8q1vimr">
            {isDiagComplete && <button onClick={resetTerminal} className="px-5 py-2.5 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-full text-sm font-semibold font-sans transition-all duration-300" data-id="element-192" data-mp="2j1e1f6asw8ov">
                Reset
              </button>}
            <button onClick={runDiagnostics} disabled={isRunning || isDiagComplete} className={`px-6 py-2.5 rounded-full text-sm font-bold font-sans transition-all duration-300 ${isRunning || isDiagComplete ? 'bg-gray-800 text-gray-600 cursor-not-allowed' : 'bg-emerald-500 hover:bg-emerald-400 text-black hover:shadow-[0_0_20px_rgba(16,185,129,0.3)] hover:scale-105'}`} data-id="element-193" data-mp="1md81uxsqk6hg">
              {isRunning ? '⏳ Diagnostika beží...' : isDiagComplete ? '✅ Dokončené' : '▶ Spustiť Master Diagnostiku'}
            </button>
          </div>
        </div>
      </div>
    </div>;
}